import { useCallback, useEffect, useState } from 'react';
import macName from '../lib/macName';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { TypewriterText } from '../hooks/useTypewriter';
import { StatusChip, formatBytes, formatCount, ago, LIVE_STATUSES, LIVE, SIGNAL } from '../components/RunCharts';
import RunSettings from '../components/RunSettings';
import RunRow from '../components/RunRow';
import DataMix, { estimate } from '../components/DataMix';

const API = '/api';
const WIDTHS = [512, 1024, 1536, 2048, 3072, 4096];
const BANDS = [16, 32, 48, 64];

function headers() {
  const h = { 'Content-Type': 'application/json' };
  const t = localStorage.getItem('logos_session_token');
  if (t) h['Authorization'] = `Bearer ${t}`;
  return h;
}

function Step({ index, title, note, children }) {
  return (
    <div className="border-t border-[#1e251f] pt-5">
      <div className="flex items-baseline gap-3 mb-3">
        <span className="font-mono text-xs text-[#555555]">{index}</span>
        <h3 className="font-mono text-sm text-white lowercase">{title}</h3>
        {note && <span className="font-mono text-xs text-[#555555] lowercase truncate">{note}</span>}
      </div>
      {children}
    </div>
  );
}

function Choice({ options, value, onChange, render = (o) => o }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(o => (
        <button key={String(o)} type="button" onClick={() => onChange(o)}
          className={`border px-3 py-1.5 rounded-full font-mono text-xs lowercase transition-all ${
            value === o ? 'border-white text-white bg-white/10' : 'border-[#383f38] text-[#888888] hover:text-white hover:border-[#888888]'}`}>
          {render(o)}
        </button>
      ))}
    </div>
  );
}

const field = 'bg-transparent border border-[#383f38] rounded-lg px-3 py-2 text-white font-mono text-sm outline-none focus:border-white transition-colors';

function OfferList({ offers, selected, onToggle, multiple }) {
  return (
    <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
      {offers.map(o => {
        const on = multiple ? selected.includes(o.id) : selected === o.id;
        return (
          <button key={o.id} type="button" onClick={() => onToggle(o)}
            className={`w-full text-left border rounded-lg px-3 py-2 font-mono text-xs grid grid-cols-[1fr_auto] sm:grid-cols-[1.3fr_0.7fr_0.6fr_0.6fr_1fr] gap-x-3 gap-y-0.5 transition-all ${
              on ? 'border-white bg-white/10 text-white' : 'border-[#1e251f] text-[#aaaaaa] hover:border-[#383f38]'}`}>
            <span className="text-white">{o.gpu_name}</span>
            <span style={{ color: LIVE }} className="text-right sm:text-left">${(o.price_per_hour ?? o.dph_total).toFixed(3)}/h</span>
            <span>{Math.round(o.gpu_ram / 1024)} GB</span>
            <span>{(o.reliability * 100).toFixed(1)}%</span>
            <span className="truncate text-[#666666]">{o.provider === 'gce' ? 'google · ' : ''}{o.geolocation}</span>
          </button>
        );
      })}
    </div>
  );
}

function SpeedTest({ options, onLaunched }) {
  const runtime = options.runtimes[0];
  const [offers, setOffers] = useState(null);
  const [picked, setPicked] = useState([]);
  const [widths, setWidths] = useState([1024, 1536, 2048]);
  const [controllers, setControllers] = useState('off');
  const [settings, setSettings] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const find = async () => {
    setError(null);
    const res = await fetch(`${API}/train/offers?min_gpu_ram_gb=10&max_dph=1.5&disk_gb=40`, { headers: headers(), credentials: 'include' });
    const body = await res.json().catch(() => []);
    if (!res.ok) { setError(body.detail || 'could not reach vast'); return; }
    setOffers(body);
  };
  const toggle = (o) => setPicked(p => p.includes(o.id) ? p.filter(x => x !== o.id) : [...p, o.id].slice(-6));
  const launch = async () => {
    const chosen = offers.filter(o => picked.includes(o.id));
    const hourly = chosen.reduce((a, o) => a + (o.price_per_hour ?? o.dph_total), 0);
    if (!window.confirm(`rent ${chosen.length} machines (${chosen.map(o => o.gpu_name).join(', ')}) for about half an hour each? together about $${(hourly * 0.5).toFixed(2)}, capped at $2 each.`)) return;
    setBusy(true); setError(null);
    for (const o of chosen) {
      const res = await fetch(`${API}/runs`, {
        method: 'POST', headers: headers(), credentials: 'include',
        body: JSON.stringify({ kind: 'benchmark', name: `speed test · ${o.gpu_name}`, runtime_id: runtime.runtime_id,
          runtime_version: runtime.version, offer_id: o.id, provider: o.provider, widths, spend_cap: 2, max_hours: 1, controllers,
          shape: { bands: 32, ...settings } }),
      });
      if (!res.ok) setError(`${o.gpu_name}: ${(await res.json().catch(() => ({}))).detail || 'failed'}`);
    }
    setBusy(false); setPicked([]);
    onLaunched();
  };

  if (!runtime) return null;
  return (
    <div className="space-y-4">
      <p className="text-[#888888] font-mono text-xs lowercase">
        rents each machine for a few minutes per width, measures it, then destroys it. nothing is saved.
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-mono text-xs text-[#555555]">widths</span>
        {WIDTHS.map(w => (
          <button key={w} type="button" onClick={() => setWidths(ws => ws.includes(w) ? ws.filter(x => x !== w) : [...ws, w].sort((a, b) => a - b))}
            className={`border px-3 py-1 rounded-full font-mono text-xs ${widths.includes(w) ? 'border-white text-white' : 'border-[#383f38] text-[#666666]'}`}>{w}</button>
        ))}
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-mono text-xs text-[#555555]">controllers</span>
        <Choice options={['off', 'on']} value={controllers} onChange={setControllers} />
        <span className="font-mono text-[11px] text-[#555555] lowercase">
          {controllers === 'off' ? 'every token predicted: measures the machine and the shape' : 'io2 decimates: measures training as it would run'}
        </span>
      </div>
      <RunSettings fields={runtime.fields} hide={['width']} onChange={setSettings} />
      {!offers ? (
        <button onClick={find} className="border border-[#383f38] px-4 py-2 rounded-lg text-white font-mono text-sm lowercase hover:bg-white hover:text-black transition-all">find machines</button>
      ) : (
        <>
          <OfferList offers={offers} selected={picked} onToggle={toggle} multiple />
          <button onClick={launch} disabled={busy || !picked.length || !widths.length}
            className="border border-white bg-white text-black px-5 py-2 rounded-lg font-mono text-sm lowercase disabled:opacity-40">
            {busy ? 'renting…' : `test ${picked.length || ''} machine${picked.length === 1 ? '' : 's'}`}
          </button>
        </>
      )}
      {error && <p className="font-mono text-xs text-[#ff5c5c] lowercase">{error}</p>}
    </div>
  );
}

function NewRun({ options, onLaunched, initialModel = '' }) {
  const navigate = useNavigate();
  const runtimes = options.runtimes;
  const [runtimeKey, setRuntimeKey] = useState(runtimes[0] ? `${runtimes[0].runtime_id}@${runtimes[0].version}` : '');
  const runtime = runtimes.find(r => `${r.runtime_id}@${r.version}` === runtimeKey);
  const [modelId, setModelId] = useState(initialModel);
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState('private');
  const [width, setWidth] = useState(1536);
  const [bands, setBands] = useState(32);
  const [data, setData] = useState({ sources: [{ id: 'fineweb-edu', share: 1 }], order: 'graded', gigabytes: 6 });
  const [offers, setOffers] = useState(null);
  const [offer, setOffer] = useState(null);
  const [settings, setSettings] = useState({});
  const [cap, setCap] = useState(options.limits.default_spend_cap);
  const [hours, setHours] = useState(24);
  const [interruptible, setInterruptible] = useState(false);
  const enabled = options.providers || ['vast'];
  const [where, setWhere] = useState(enabled.includes('gce') ? 'gce' : 'any');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const models = options.models.filter(m => !runtime || m.runtime_id === runtime.runtime_id);

  const find = async (spot = interruptible, provider = where) => {
    setError(null); setOffer(null);
    const res = await fetch(`${API}/train/offers?min_gpu_ram_gb=16&max_dph=3&disk_gb=120&interruptible=${spot}&provider=${provider}`, { headers: headers(), credentials: 'include' });
    const body = await res.json().catch(() => []);
    if (!res.ok) { setError(body.detail || 'could not find machines'); return; }
    setOffers(body);
  };

  const price = offer ? (offer.price_per_hour ?? offer.dph_total) : null;
  const hoursAffordable = offer ? cap / price : null;
  const model = models.find(m => m.checkpoint_id === modelId);
  // a continued model shows its own saved settings: shape locked, dynamics as they were left.
  // a setting it predates shows what the runtime assumes for it on load (`absent`)
  const saved = model ? Object.fromEntries((runtime?.fields || [])
    .map(f => [f.key, model.config?.[f.key] ?? f.absent])
    .filter(([, v]) => v !== undefined && v !== null)) : {};
  const reading = estimate({ gigabytes: data.gigabytes, offer, width: model ? (Number(model.width) || Number(model.config?.width) || 1536) : width, speeds: options.speeds });
  const launch = async () => {
    const lasts = Math.min(hours, hoursAffordable).toFixed(1);
    if (!window.confirm(`rent ${interruptible ? 'an interruptible' : 'a'} ${offer.gpu_name} at $${price.toFixed(3)}/h and train ${modelId ? 'the model further' : 'a new model'}? it stops at $${cap} or ${hours}h (about ${lasts}h on this machine), saving first.`)) return;
    setBusy(true); setError(null);
    const body = {
      name: modelId ? `${models.find(m => m.checkpoint_id === modelId)?.name} · continued` : (name || 'cloud run'),
      runtime_id: runtime.runtime_id, runtime_version: runtime.version, data,
      offer_id: offer.id, provider: offer.provider, spend_cap: Number(cap), max_hours: Number(hours), visibility, interruptible,
    };
    if (modelId) body.checkpoint_id = modelId;
    body.shape = modelId ? settings : { ...settings, width, bands };
    const res = await fetch(`${API}/runs`, { method: 'POST', headers: headers(), credentials: 'include', body: JSON.stringify(body) });
    const answer = await res.json().catch(() => ({}));
    setBusy(false);
    if (!res.ok) { setError(answer.detail || 'the run did not start'); return; }
    onLaunched();
    navigate(`/runs/${answer.run_id}`);
  };

  if (!runtimes.length) return <p className="text-[#888888] font-mono text-xs lowercase">no runtime has a cloud bundle yet.</p>;
  return (
    <div className="space-y-5">
      <Step index="1" title="model" note={modelId ? 'continues from its head' : 'a new model starts private'}>
        <div className="space-y-3">
          <select value={modelId} onChange={e => setModelId(e.target.value)} className={`${field} w-full sm:w-auto`}>
            <option value="">a new model</option>
            {models.map(m => <option key={m.checkpoint_id} value={m.checkpoint_id}>{m.name} · {formatBytes(m.bytes_seen)} seen · {m.visibility}</option>)}
          </select>
          {!modelId && (
            <div className="flex gap-3 flex-wrap items-center">
              <input value={name} onChange={e => setName(e.target.value)} placeholder="name" className={`${field} w-full sm:w-72`} />
              <Choice options={['private', 'unlisted']} value={visibility} onChange={setVisibility} />
            </div>
          )}
        </div>
      </Step>
      <Step index="2" title="runtime">
        <Choice options={runtimes.map(r => `${r.runtime_id}@${r.version}`)} value={runtimeKey} onChange={setRuntimeKey}
          render={(k) => { const r = runtimes.find(x => `${x.runtime_id}@${x.version}` === k); return `${r.name} ${r.version}`; }} />
      </Step>
      {!modelId && (
        <Step index="3" title="shape">
          <div className="space-y-2">
            <div className="flex items-center gap-3 flex-wrap"><span className="font-mono text-xs text-[#555555] w-14">width</span><Choice options={WIDTHS} value={width} onChange={setWidth} />
              <input inputMode="numeric" placeholder="custom" value={WIDTHS.includes(width) ? '' : width}
                onChange={(e) => setWidth(Number(e.target.value) || WIDTHS[2])} className={`${field} w-24`} /></div>
            <div className="flex items-center gap-3 flex-wrap"><span className="font-mono text-xs text-[#555555] w-14">bands</span><Choice options={BANDS} value={bands} onChange={setBands} />
              <input inputMode="numeric" placeholder="custom" value={BANDS.includes(bands) ? '' : bands}
                onChange={(e) => setBands(Number(e.target.value) || 32)} className={`${field} w-24`} /></div>
          </div>
        </Step>
      )}
      <div className="border-t border-[#1e251f] pt-5">
        <RunSettings key={`${runtimeKey}-${modelId}`} fields={runtime?.fields || []} lockShape={!!modelId} current={saved}
          hide={modelId ? [] : ['width', 'bands']} onChange={setSettings} />
      </div>
      <Step index={modelId ? '3' : '4'} title="data" note={`${data.gigabytes} gb from ${data.sources.length} source${data.sources.length === 1 ? '' : 's'}`}>
        <DataMix catalogue={options.catalogue || []} orders={options.orders || {}} value={data} onChange={setData} mine={options.sources || []} />
        <Link to="/data" className="inline-block mt-2 font-mono text-[11px] text-[#777777] hover:text-white lowercase">add your own: links and files →</Link>
      </Step>
      <Step index={modelId ? '4' : '5'} title="machine" note={offer ? `${offer.gpu_name} · $${price.toFixed(3)}/h` : null}>
        {enabled.length > 1 && (
          <div className="mb-3">
            <Choice options={[...enabled, 'any']} value={where} render={(p) => ({ gce: 'google', vast: 'vast', any: 'anywhere' }[p] || p)}
              onChange={(p) => { setWhere(p); if (offers) find(interruptible, p); }} />
          </div>
        )}
        <div className="flex items-center gap-3 flex-wrap mb-3">
          <Choice options={['on-demand', 'interruptible']} value={interruptible ? 'interruptible' : 'on-demand'}
            onChange={(v) => { const spot = v === 'interruptible'; setInterruptible(spot); if (offers) find(spot); }} />
          <span className="font-mono text-[11px] text-[#555555] lowercase">
            {interruptible ? 'cheaper. if it is outbid, the run moves to another machine from its last save.' : 'yours until the run ends.'}
          </span>
        </div>
        {!offers ? (
          <button type="button" onClick={() => find()} className="border border-[#383f38] px-4 py-2 rounded-lg text-white font-mono text-sm lowercase hover:bg-white hover:text-black transition-all">find machines</button>
        ) : <OfferList offers={offers} selected={offer?.id} onToggle={setOffer} />}
      </Step>
      <Step index={modelId ? '5' : '6'} title="limits" note={`$${options.limits.month_available.toFixed(2)} left this month`}>
        <div className="flex gap-4 flex-wrap items-center font-mono text-sm">
          <label className="flex items-center gap-2 text-[#888888]">$<input type="number" min="1" max={options.limits.month_available} value={cap} onChange={e => setCap(e.target.value)} className={`${field} w-24`} /> cap</label>
          <label className="flex items-center gap-2 text-[#888888]"><input type="number" min="1" max={options.limits.longest_run_hours} value={hours} onChange={e => setHours(e.target.value)} className={`${field} w-20`} /> hours</label>
          {hoursAffordable !== null && <span className="text-[#888888] text-xs">about {Math.min(hours, hoursAffordable).toFixed(1)}h on this machine</span>}
        </div>
        {offer && (
          <p className="font-mono text-[11px] text-[#666666] lowercase mt-2" data-testid="read-estimate">
            {reading
              ? `reading all ${data.gigabytes} gb takes about ${reading.hours.toFixed(1)}h on ${offer.gpu_name} (≈ $${reading.dollars.toFixed(2)}), going by its speed test at width ${reading.width}.`
              : `no speed test on ${offer.gpu_name} yet, so no estimate of how long ${data.gigabytes} gb takes.`}
          </p>
        )}
      </Step>
      <div className="border-t border-[#1e251f] pt-5 flex items-center gap-4 flex-wrap">
        <button onClick={launch} disabled={busy || !offer || !runtime || cap > options.limits.month_available}
          className="border border-white bg-white text-black px-6 py-2.5 rounded-lg font-mono text-sm lowercase hover:bg-transparent hover:text-white transition-all disabled:opacity-40"
          data-testid="train-launch">
          {busy ? 'renting…' : 'launch'}
        </button>
        {error && <span className="font-mono text-xs text-[#ff5c5c] lowercase">{error}</span>}
      </div>
    </div>
  );
}

const LIVE_DOT = 'rgb(77, 255, 133)';

async function command(tokenId, kind, args) {
  const res = await fetch(`${API}/me/apps/${tokenId}/commands`, {
    method: 'POST', headers: headers(), credentials: 'include', body: JSON.stringify({ kind, args }),
  });
  const body = await res.json().catch(() => ({}));
  return res.ok ? { ok: true, body } : { ok: false, error: body.detail || 'the app could not be asked' };
}

// a model training on one of your macs, as its app last reported it
function LocalRunRow({ app, onAsked }) {
  const t = app.state?.training || {};
  const [asked, setAsked] = useState(null);
  const stop = async () => {
    const r = await command(app.token_id, 'train.stop', { save: true });
    setAsked(r.ok ? 'asked to save and stop' : r.error);
    onAsked();
  };
  return (
    <div className="py-4" data-testid="local-run-row">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-mono text-sm text-white lowercase">{t.name || 'a model'}</span>
        <span className="font-mono text-[10px] lowercase px-2 py-0.5 rounded border border-[#383f38] text-[#bbbbbb]">on {macName(app.name)}</span>
        {typeof t.recent_bpb === 'number' && <span className="font-mono text-xs" style={{ color: LIVE }}>{t.recent_bpb.toFixed(3)} bpb</span>}
        {!app.online && <span className="font-mono text-[11px] text-[#666666] lowercase">app offline · as last reported</span>}
        <span className="ml-auto flex gap-2">
          <Link to={`/remote/${app.token_id}`} className="font-mono text-xs text-[#888888] hover:text-white lowercase">open →</Link>
          <button onClick={stop} disabled={!app.online || Boolean(asked)}
            className="font-mono text-xs text-white border border-[#383f38] rounded-full px-3 py-0.5 hover:bg-white hover:text-black transition-all disabled:opacity-40">save and stop</button>
        </span>
      </div>
      <div className="flex gap-4 flex-wrap mt-1.5 font-mono text-xs text-[#888888]">
        <span>free · on your mac</span>
        {t.bytes_seen ? <span>{formatBytes(t.bytes_seen)} seen</span> : null}
        {asked && <span className="text-white">{asked}</span>}
      </div>
      {typeof t.progress === 'number' && (
        <div className="h-0.5 bg-[#1e251f] rounded-full overflow-hidden mt-2">
          <div className="h-full" style={{ width: `${Math.min(100, t.progress * 100)}%`, background: LIVE }} />
        </div>
      )}
    </div>
  );
}

// resume a model on one of your macs
function OnYourMac({ apps, onAsked }) {
  const [pick, setPick] = useState({});
  const [note, setNote] = useState({});
  if (!apps.length) {
    return (
      <p className="text-[#888888] text-sm">
        link the soma app to train on your own mac from here, free: <Link to="/downloads" className="text-white hover:underline">download soma</Link>,
        then sign in from its logOS page.
      </p>
    );
  }
  return (
    <div className="space-y-3">
      {apps.map(app => {
        const state = app.state || {};
        const models = (state.checkpoints || []).filter(c => c.train_ok !== false);
        const busy = state.training?.running ? 'already training' : state.chat?.loaded ? 'chat is loaded' : null;
        const chosen = pick[app.token_id] || models[0]?.id || '';
        const go = async () => {
          const r = await command(app.token_id, 'train.resume', { checkpoint: chosen });
          setNote(n => ({ ...n, [app.token_id]: r.ok ? `asked ${macName(app.name)} to resume ${chosen}` : r.error }));
          onAsked();
        };
        return (
          <div key={app.token_id} className="flex items-center gap-3 flex-wrap" data-testid="mac-start">
            <span className="w-2 h-2 rounded-full" style={{ background: app.online ? LIVE_DOT : '#444444' }} />
            <span className="font-mono text-sm text-white lowercase">{macName(app.name)}</span>
            {models.length ? (
              <>
                <select value={chosen} onChange={e => setPick(p => ({ ...p, [app.token_id]: e.target.value }))}
                  className="bg-transparent border border-[#383f38] rounded-lg px-2 py-1.5 font-mono text-xs text-white outline-none focus:border-white">
                  {models.map(m => <option key={m.id} value={m.id} className="bg-black">{m.name}{m.bytes_seen ? ` · ${formatBytes(m.bytes_seen)} seen` : ''}</option>)}
                </select>
                <button onClick={go} disabled={!app.online || Boolean(busy)}
                  className="border border-white bg-white text-black px-4 py-1.5 rounded-full font-mono text-xs lowercase hover:bg-transparent hover:text-white transition-all disabled:opacity-40">
                  resume training
                </button>
              </>
            ) : <span className="font-mono text-xs text-[#666666] lowercase">no models on it yet</span>}
            <span className="font-mono text-[11px] text-[#666666] lowercase">
              {!app.online ? 'offline' : busy || ''}{note[app.token_id] ? ` ${note[app.token_id]}` : ''}
            </span>
          </div>
        );
      })}
      <p className="font-mono text-[11px] text-[#555555] lowercase">continues from the model's last save with its own settings. start new models in the app.</p>
    </div>
  );
}

export default function Train() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const [runs, setRuns] = useState([]);
  const [apps, setApps] = useState([]);
  const [limits, setLimits] = useState(null);
  const [options, setOptions] = useState(null);
  const [open, setOpen] = useState(params.get('model') ? 'run' : null);   // 'run' | 'speed'
  const cloud = user?.role === 'admin';

  const load = useCallback(async () => {
    const [r, a] = await Promise.all([
      fetch(`${API}/runs`, { headers: headers(), credentials: 'include' }).then(x => (x.ok ? x.json() : null)),
      fetch(`${API}/me/apps`, { headers: headers(), credentials: 'include' }).then(x => (x.ok ? x.json() : null)),
    ]);
    if (r) { setRuns(r.runs); setLimits(r.limits); }
    if (a) setApps(a.apps);
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);
  useEffect(() => {
    if (!cloud) return;
    fetch(`${API}/train/options`, { headers: headers(), credentials: 'include' })
      .then(r => r.ok ? r.json() : null).then(setOptions);
  }, [cloud]);
  const live = runs.filter(r => LIVE_STATUSES.includes(r.status));
  const localLive = apps.filter(a => a.state?.training?.running);
  const anyLive = live.length > 0 || localLive.length > 0;
  useEffect(() => {
    if (!user) return undefined;
    const timer = setInterval(load, anyLive ? 5000 : 20000);
    return () => clearInterval(timer);
  }, [user, anyLive, load]);

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><p className="text-[#888888] lowercase animate-pulse">loading...</p></div>;
  if (!user) return <div className="min-h-screen bg-black flex items-center justify-center px-6"><p className="text-[#888888] lowercase">sign in to train</p></div>;

  const past = runs.filter(r => !LIVE_STATUSES.includes(r.status));
  const tests = past.filter(r => r.kind === 'benchmark' && r.status === 'completed');
  const lastLocal = apps.filter(a => !a.state?.training?.running && a.state?.training?.name && a.state?.training?.status !== 'idle');

  return (
    <div className="min-h-screen bg-black py-10 px-4 sm:px-6 lg:px-8 page-wrapper" data-testid="train-page">
      <div className="max-w-4xl mx-auto space-y-10">
        <div>
          <h1 className="text-4xl lg:text-5xl font-bold text-white lowercase font-mono"><TypewriterText text="train" speed={65} /></h1>
          <p className="text-[#aaaaaa] mt-3">
            train on your own mac, free, or in the cloud when your mac isn't enough. everything you train is private until you publish it.
          </p>
        </div>

        <section className="space-y-3" data-testid="train-running">
          <h2 className="font-mono text-sm text-white lowercase border-b border-[#1e251f] pb-2">running now{live.length + localLive.length ? ` · ${live.length + localLive.length}` : ''}</h2>
          {live.length + localLive.length ? (
            <div className="divide-y divide-[#1a201b]">
              {localLive.map(a => <LocalRunRow key={a.token_id} app={a} onAsked={load} />)}
              {live.map(r => <RunRow key={r.run_id} run={r} />)}
            </div>
          ) : <p className="text-[#555555] font-mono text-xs lowercase">nothing is training</p>}
        </section>

        <section className="space-y-4" data-testid="train-start">
          <h2 className="font-mono text-sm text-white lowercase border-b border-[#1e251f] pb-2">start training</h2>
          <div className="space-y-2">
            <h3 className="font-mono text-[11px] text-[#666666] lowercase tracking-wide">on your mac · free</h3>
            <OnYourMac apps={apps} onAsked={load} />
          </div>
          <div className="space-y-2">
            <h3 className="font-mono text-[11px] text-[#666666] lowercase tracking-wide">
              in the cloud{limits && cloud ? ` · $${limits.month_committed.toFixed(2)} of $${limits.monthly_cap} committed this month` : ''}
            </h3>
            {cloud ? (
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => setOpen(open === 'run' ? null : 'run')}
                  className={`border px-5 py-2 rounded-lg font-mono text-sm lowercase transition-all ${open === 'run' ? 'border-white bg-white text-black' : 'border-[#383f38] text-white hover:border-white'}`}
                  data-testid="train-new">new cloud run</button>
                <button onClick={() => setOpen(open === 'speed' ? null : 'speed')}
                  className={`border px-5 py-2 rounded-lg font-mono text-sm lowercase transition-all ${open === 'speed' ? 'border-white bg-white text-black' : 'border-[#383f38] text-white hover:border-white'}`}>speed test</button>
              </div>
            ) : (
              <p className="text-[#888888] text-sm">cloud training opens soon: bigger models and bigger data than a mac can hold, without keeping it awake.</p>
            )}
          </div>
          {open && options && (
            <div className="border border-[#383f38] rounded-xl p-4 sm:p-6">
              {open === 'run' ? <NewRun options={options} onLaunched={load} initialModel={params.get('model') || ''} />
                : <SpeedTest options={options} onLaunched={() => { setOpen(null); load(); }} />}
            </div>
          )}
          {open && !options && <p className="text-[#888888] font-mono text-xs lowercase animate-pulse">loading options…</p>}
        </section>

        {tests.length > 0 && (
          <section className="space-y-3">
            <h2 className="font-mono text-sm text-white lowercase border-b border-[#1e251f] pb-2">speed tests</h2>
            <SpeedResults runs={tests} />
          </section>
        )}

        <section className="space-y-3" data-testid="train-recent">
          <h2 className="font-mono text-sm text-white lowercase border-b border-[#1e251f] pb-2">recent</h2>
          {lastLocal.length + past.length ? (
            <div className="divide-y divide-[#1a201b]">
              {lastLocal.map(a => (
                <div key={a.token_id} className="py-4 flex items-baseline gap-3 flex-wrap font-mono">
                  <span className="text-sm text-white lowercase">{a.state.training.name}</span>
                  <span className="text-[10px] lowercase px-2 py-0.5 rounded border border-[#383f38] text-[#bbbbbb]">on {macName(a.name)}</span>
                  <span className="text-xs text-[#888888] lowercase">{a.state.training.status}</span>
                  <Link to={`/remote/${a.token_id}`} className="ml-auto text-xs text-[#888888] hover:text-white lowercase">open →</Link>
                </div>
              ))}
              {past.map(r => <RunRow key={r.run_id} run={r} />)}
            </div>
          ) : <p className="text-[#555555] font-mono text-xs lowercase">no training yet</p>}
        </section>
      </div>
    </div>
  );
}

function SpeedResults({ runs }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    Promise.all(runs.slice(0, 12).map(r => fetch(`${API}/runs/${r.run_id}`, { headers: headers(), credentials: 'include' })
      .then(x => x.ok ? x.json() : null)))
      .then(all => setRows(all.filter(Boolean).flatMap(run => ((run.latest || {}).benchmark || [])
        .filter(b => b.bps).map(b => ({ ...b, gpu: b.gpu || run.gpu_name, dph: run.dollars_per_hour, run_id: run.run_id })))));
  }, [runs]);
  if (!rows.length) return null;
  // what a dollar buys: source bytes per dollar
  const scored = rows.map(r => ({ ...r, perDollar: r.bps * 3600 / Math.max(0.001, r.dph) }))
    .sort((a, b) => a.width - b.width || b.perDollar - a.perDollar);
  return (
    <div className="font-mono text-xs overflow-x-auto">
      <div className="grid grid-cols-[1.4fr_0.6fr_0.8fr_0.8fr_0.9fr] gap-3 text-[#555555] pb-1 min-w-[520px]">
        <span>gpu</span><span>width</span><span>bytes/s</span><span>$/h</span><span>bytes per $</span>
      </div>
      {scored.map((r, i) => (
        <Link key={i} to={`/runs/${r.run_id}`} className="grid grid-cols-[1.4fr_0.6fr_0.8fr_0.8fr_0.9fr] gap-3 py-1 text-[#aaaaaa] hover:text-white min-w-[520px]">
          <span className="text-white truncate">{r.gpu}</span><span>{r.width}</span><span>{formatBytes(r.bps)}</span>
          <span>${(r.dph || 0).toFixed(3)}</span><span style={{ color: LIVE }}>{formatBytes(r.perDollar)}</span>
        </Link>
      ))}
      <p className="text-[#555555] mt-2 lowercase">{formatCount(scored.length)} measurements. bytes per $ is what an hour of each machine buys.</p>
    </div>
  );
}
